import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLiveTime, formatLiveElapsed } from '../hooks/useLiveTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 4000;

const STATUS_FLOW = {
  awaiting_confirmation: { label: '⚠️ Təsdiq gözləyir', color: 'bg-rose-600', next: null, nextLabel: null },
  pending: { label: 'Yeni', color: 'bg-amber-500', next: 'preparing', nextLabel: 'Hazırla' },
  preparing: { label: 'Hazırlanır', color: 'bg-blue-500', next: 'ready', nextLabel: 'Hazır' },
  ready: { label: 'Hazır', color: 'bg-emerald-500', next: 'served', nextLabel: 'Verildi' },
  served: { label: 'Verildi', color: 'bg-stone-500', next: 'completed', nextLabel: 'Tamamla' },
  completed: { label: 'Tamamlandı', color: 'bg-stone-400', next: null },
  cancelled: { label: 'Ləğv edildi', color: 'bg-red-500', next: null },
  rejected: { label: 'Rədd edildi', color: 'bg-red-700', next: null },
};

export const KitchenDisplay = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const lastOrderCount = useRef(0);
  const audioRef = useRef(null);
  const now = useLiveTime(1000);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [restaurantId]);

  const fetchAll = async () => {
    try {
      const [orderRes, statRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}?status=kitchen`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}/stats`, { withCredentials: true }),
      ]);
      const newOrders = orderRes.data;
      // Beep on new order
      if (lastOrderCount.current > 0 && newOrders.length > lastOrderCount.current) {
        try { audioRef.current?.play().catch(() => {}); } catch { /* ignore */ }
      }
      lastOrderCount.current = newOrders.length;
      setOrders(newOrders);
      setStats(statRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Yüklənmə xətası');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      await fetchAll();
    } catch (err) {
      alert('Status dəyişdirilə bilmədi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingId(null);
    }
  };

  const confirmAction = async (orderId, action, reason) => {
    setUpdatingId(orderId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/orders/${orderId}/confirm`,
        { action, reason: reason || null },
        { withCredentials: true }
      );
      await fetchAll();
    } catch (err) {
      alert('Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Beep audio (data URI silent — browsers limit autoplay; this is best-effort) */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=" preload="auto" />

      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-stone-800 border-b border-stone-700 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-stone-400 hover:text-white"
            data-testid="kitchen-back-btn"
          >← Dashboard</button>
          <h1 className="text-xl font-bold flex items-center gap-2" data-testid="kitchen-title">
            🍳 Mətbəx Ekranı
          </h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {stats && (
            <>
              <Stat label="Aktiv" value={stats.active_orders} accent="text-amber-400" />
              <Stat label="Bu gün" value={stats.today_orders} accent="text-blue-400" />
              <Stat label="Gəlir" value={`${stats.currency_symbol}${stats.today_revenue}`} accent="text-emerald-400" />
            </>
          )}
          <div className="text-xs text-stone-400">Avtomatik yenilənir • {POLL_INTERVAL_MS / 1000}s</div>
        </div>
      </header>

      {error && (
        <div className="px-5 py-3 bg-red-900/40 text-red-200 text-sm" data-testid="kitchen-error">⚠️ {error}</div>
      )}

      <div className="p-5">
        {orders.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🍽️</div>
            <p className="text-stone-400 text-xl">Hələ aktiv sifariş yoxdur</p>
            <p className="text-stone-500 text-sm mt-2">Yeni sifariş gələndə burada görünəcək</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="kitchen-orders-grid">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                updating={updatingId === order.id}
                onUpdate={updateStatus}
                onConfirmAction={confirmAction}
                now={now}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="text-center">
    <div className={`text-xl font-bold ${accent}`}>{value}</div>
    <div className="text-[10px] text-stone-400 uppercase">{label}</div>
  </div>
);

const LiveTimestamp = ({ iso, now }) => {
  const { exact, elapsed } = formatLiveElapsed(iso, now);
  return (
    <span className="font-mono" data-testid="live-timestamp">
      <span className="text-stone-300">{exact}</span>
      <span className="mx-1 opacity-50">•</span>
      <span className="text-emerald-300">{elapsed}</span>
    </span>
  );
};

const OrderCard = ({ order, updating, onUpdate, onConfirmAction, now }) => {
  const cfg = STATUS_FLOW[order.status] || STATUS_FLOW.pending;
  const totalQty = order.items.reduce((a, i) => a + (i.quantity || 0), 0);
  const sec = order.security_check || {};
  const geoInfo = sec.geo || {};
  const isAwaiting = order.status === 'awaiting_confirmation';

  return (
    <div className={`bg-stone-800 rounded-2xl border-2 ${isAwaiting ? 'border-rose-500 animate-pulse-slow' : order.status === 'pending' ? 'border-amber-500 animate-pulse-slow' : 'border-stone-700'} overflow-hidden`} data-testid={`kitchen-order-${order.id}`}>
      <div className={`${cfg.color} px-4 py-3 flex items-center justify-between`}>
        <div>
          <div className="text-xs uppercase opacity-90 font-bold">{cfg.label}</div>
          <div className="text-lg font-black">{order.order_number}</div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black">M{order.table_number}</div>
          <div className="text-[10px] uppercase opacity-90">Masa</div>
        </div>
      </div>

      <div className="p-4">
        <div className="text-xs text-stone-400 mb-2 flex justify-between">
          <LiveTimestamp iso={order.created_at} now={now} />
          <span>{totalQty} məhsul</span>
        </div>

        {/* Security info (only for awaiting_confirmation) */}
        {isAwaiting && (
          <div className="mb-3 bg-rose-900/30 border border-rose-700 rounded-lg p-2 text-xs space-y-0.5" data-testid={`security-info-${order.id}`}>
            {geoInfo.supplied && geoInfo.distance_m != null && (
              <div className={`flex items-center gap-1 ${geoInfo.within_radius ? 'text-emerald-300' : 'text-rose-300'}`}>
                <span>📍</span>
                <span>Məsafə: ~{Math.round(geoInfo.distance_m)}m</span>
                {geoInfo.within_radius ? <span className="text-[10px] bg-emerald-700/50 px-1.5 rounded">RADIUS DAXİLİ</span> : <span className="text-[10px] bg-rose-700 px-1.5 rounded">UZAQ</span>}
              </div>
            )}
            {!geoInfo.supplied && geoInfo.enabled && (
              <div className="text-rose-300">⚠️ GPS göndərilməyib</div>
            )}
            {order.customer_ip && (
              <div className="text-stone-300 text-[10px]">🌐 IP: {order.customer_ip}</div>
            )}
          </div>
        )}

        <ul className="space-y-1.5 mb-3 max-h-44 overflow-y-auto">
          {order.items.map((it, idx) => (
            <li key={idx} className="flex justify-between text-sm border-b border-stone-700 pb-1.5">
              <span className="flex-1 pr-2">
                <span className="font-bold text-emerald-400">{it.quantity}x</span> {it.name_az || it.name_en}
              </span>
              <span className="text-stone-400 text-xs">{order.currency_symbol}{(it.line_total || it.unit_price * it.quantity).toFixed(2)}</span>
            </li>
          ))}
        </ul>

        {order.notes && (
          <div className="text-xs bg-amber-900/30 border border-amber-700 text-amber-200 rounded-lg p-2 mb-3" data-testid={`order-notes-${order.id}`}>
            📝 {order.notes}
          </div>
        )}

        {order.customer_name && (
          <div className="text-xs text-stone-400 mb-3">
            👤 {order.customer_name} {order.customer_phone && `• ${order.customer_phone}`}
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-stone-400">Cəm:</span>
          <span className="text-lg font-bold text-emerald-400">{order.currency_symbol}{order.total}</span>
        </div>

        {/* Action buttons */}
        {isAwaiting ? (
          <div className="flex gap-2">
            <button
              onClick={() => onConfirmAction(order.id, 'confirm')}
              disabled={updating}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50"
              data-testid={`order-confirm-${order.id}`}
            >{updating ? '...' : '✓ Təsdiq et'}</button>
            <button
              onClick={() => onConfirmAction(order.id, 'reject', 'Saxta sifariş')}
              disabled={updating}
              className="flex-1 py-2.5 rounded-xl bg-red-700 hover:bg-red-800 text-white font-bold text-sm disabled:opacity-50"
              data-testid={`order-reject-${order.id}`}
            >✕ Rədd et</button>
          </div>
        ) : (
          <div className="flex gap-2">
            {cfg.next && (
              <button
                onClick={() => onUpdate(order.id, cfg.next)}
                disabled={updating}
                className={`flex-1 py-2.5 rounded-xl text-white font-bold text-sm ${cfg.color} hover:opacity-90 disabled:opacity-50 transition`}
                data-testid={`order-next-${order.id}`}
              >
                {updating ? '...' : `→ ${cfg.nextLabel}`}
              </button>
            )}
            {order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'rejected' && (
              <button
                onClick={() => onUpdate(order.id, 'cancelled')}
                disabled={updating}
                className="px-3 py-2.5 bg-red-900/40 hover:bg-red-900/70 text-red-200 rounded-xl text-sm font-semibold border border-red-800 disabled:opacity-50"
                data-testid={`order-cancel-${order.id}`}
              >
                Ləğv
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
